/**
 * Utility to calculate cart weights, chargeable weight, and check against max configured weight slabs.
 */

/**
 * Get the maximum configured weight limit from settings weight slabs.
 * @param {Object} settings Global settings object
 * @returns {number} Maximum allowed weight in KG (0 if not configured)
 */
export const getMaxConfiguredWeight = (settings) => {
  const slabs = settings?.weightSlabs || settings?.settings?.weightSlabs || [];
  if (!Array.isArray(slabs) || slabs.length === 0) return 0;
  const maxSlab = slabs.reduce((max, s) => {
    const val = Number(s?.maxWeight) || 0;
    return val > max ? val : max;
  }, 0);
  return maxSlab;
};

/**
 * Calculate total weight in KG from cart items.
 * @param {Array} cartItems Array of cart items
 * @returns {{ totalGrams: number, totalKg: number }}
 */
export const calculateCartWeight = (cartItems = []) => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return { totalGrams: 0, totalKg: 0 };
  }

  let totalGrams = 0;

  cartItems.forEach((item) => {
    if (Array.isArray(item.products) && item.products.length > 0) {
      item.products.forEach((p) => {
        const qty =
          Number(p.totalQuantity) ||
          Number(p.total_quantity) ||
          Number(p.quantity) ||
          Number(item.quantity) ||
          1;
        const weight =
          Number(p.productWeight) ||
          Number(p.product?.weight) ||
          Number(p.weight) ||
          0;
        totalGrams += qty * weight;
      });
    } else {
      const qty = Number(item.quantity) || 1;
      const weight = Number(item.bundle?.weight) || Number(item.weight) || 0;
      totalGrams += qty * weight;
    }
  });

  const totalKg = totalGrams > 0 ? Number((totalGrams / 1000).toFixed(2)) : 0;
  return { totalGrams, totalKg };
};

/**
 * Calculate weight for a single bundle / item being added.
 * @param {Object} bundle Bundle object
 * @param {Array} cartItemProducts Cart item products
 * @param {number} quantity Overall quantity
 * @returns {{ grams: number, kg: number }}
 */
export const calculateItemAdditionWeight = (bundle, cartItemProducts = [], quantity = 1) => {
  let grams = 0;
  const qty = Number(quantity) || 1;

  if (Array.isArray(cartItemProducts) && cartItemProducts.length > 0) {
    cartItemProducts.forEach((p) => {
      const productQty = Number(p.total_quantity) || Number(p.quantity) || 1;
      const matchingProduct = bundle?.products?.find(
        (bp) => (bp.product_id || bp.id) === (p.product_id || p.productId)
      );
      const weight =
        Number(p.weight) ||
        Number(matchingProduct?.weight) ||
        Number(matchingProduct?.product?.weight) ||
        0;
      grams += productQty * weight;
    });
  } else if (Array.isArray(bundle?.products) && bundle.products.length > 0) {
    bundle.products.forEach((p) => {
      const pQty = Number(p.quantity) || 1;
      const weight = Number(p.weight) || Number(p.product?.weight) || 0;
      grams += pQty * weight * qty;
    });
  } else {
    const weight = Number(bundle?.weight) || 0;
    grams += weight * qty;
  }

  const kg = grams > 0 ? Number((grams / 1000).toFixed(2)) : 0;
  return { grams, kg };
};
